import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart3,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Home,
  Shield,
  Star,
  Users,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useHistory, useLocation } from 'react-router-dom';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import userRoles from '../constants/userRoles';
import UserContext from '../hooks/context/UserContext';
import { cn } from '../lib/utils';
import Header from './Header';

const ADMIN_NAV_ITEMS = [
  { path: '/admin/dashboard', title: 'Dashboard', icon: BarChart3 },
  { path: '/admin/departments', title: 'Departments', icon: Building2 },
  { path: '/admin/users', title: 'Users', icon: Users },
  { path: '/admin/reviews', title: 'Reviews', icon: Star },
  { path: '/admin/applications', title: 'Applications', icon: FileText },
];

const SIDEBAR_COLLAPSED_KEY = 'grow-admin-sidebar-collapsed';

function NavItem({ item, isCollapsed, isActive, onClick }) {
  const Icon = item.icon;

  const content = (
    <button
      type="button"
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-3 h-10 text-sm font-medium transition-colors',
        'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
        isCollapsed && 'justify-center px-2',
        isActive && 'bg-sidebar-accent text-sidebar-foreground',
      )}
      onClick={onClick}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {!isCollapsed && <span className="truncate">{item.title}</span>}
    </button>
  );

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {item.title}
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

NavItem.propTypes = {
  item: PropTypes.shape({
    path: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
  }).isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

function AdminSidebar({ isCollapsed, onToggle, isHead }) {
  const history = useHistory();
  const location = useLocation();

  const handleNavigation = (path) => {
    history.push(path);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64',
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo Section */}
          <div
            className={cn(
              'flex h-16 items-center border-b border-sidebar-border px-4',
              isCollapsed ? 'justify-center' : 'justify-between',
            )}
          >
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                  <Shield className="h-4 w-4 text-primary-foreground" />
                </div>
                <div>
                  <span className="text-lg font-semibold text-sidebar-foreground">Grow</span>
                  <span className="ml-1 text-xs text-muted-foreground">Admin</span>
                </div>
              </div>
            )}
            {isCollapsed && (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <Shield className="h-4 w-4 text-primary-foreground" />
              </div>
            )}
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            {!isCollapsed && (
              <p className="px-3 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Admin Menu
              </p>
            )}

            <nav className="flex flex-col gap-1">
              {ADMIN_NAV_ITEMS.map((item) => (
                <NavItem
                  key={item.path}
                  item={item}
                  isCollapsed={isCollapsed}
                  isActive={location.pathname === item.path}
                  onClick={() => handleNavigation(item.path)}
                />
              ))}
            </nav>

            {/* Panel Links Section */}
            <div className={cn('my-4 border-t border-sidebar-border', isCollapsed && 'mx-2')} />
            {!isCollapsed && (
              <p className="px-3 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Switch Panel
              </p>
            )}
            <nav className="flex flex-col gap-1">
              <NavItem
                item={{ path: '/home', title: 'Users Panel', icon: Home }}
                isCollapsed={isCollapsed}
                isActive={false}
                onClick={() => handleNavigation('/home')}
              />
              {isHead && (
                <NavItem
                  item={{ path: '/head/departments', title: 'Head Panel', icon: Building2 }}
                  isCollapsed={isCollapsed}
                  isActive={false}
                  onClick={() => handleNavigation('/head/departments')}
                />
              )}
            </nav>
          </ScrollArea>

          {/* Collapse Toggle */}
          <div
            className={cn('border-t border-sidebar-border p-3', !isCollapsed && 'flex justify-end')}
          >
            <button
              type="button"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
              )}
              onClick={onToggle}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}

AdminSidebar.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  isHead: PropTypes.bool.isRequired,
};

function AdminLayout({ children, title }) {
  const { user } = useContext(UserContext);
  const isHead = user?.roles?.includes(userRoles.HEAD);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  return (
    <>
      <Helmet>
        <title>{title} | Grow Admin</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <AdminSidebar isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} isHead={isHead} />
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              className="fixed inset-0 z-30 bg-black/50 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="fixed left-0 top-0 z-40 lg:hidden">
              <AdminSidebar
                isCollapsed={false}
                onToggle={() => setIsMobileMenuOpen(false)}
                isHead={isHead}
              />
            </div>
          </>
        )}

        {/* Main Content */}
        <div
          className={cn(
            'transition-all duration-300 min-h-screen flex flex-col',
            isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64',
          )}
        >
          <Header title={title} onMenuClick={toggleMobileMenu} showMenuButton />
          <main className="flex-1 overflow-auto">{children}</main>
        </div>
      </div>
    </>
  );
}

AdminLayout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
};

export default AdminLayout;
